import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Alert,
  Spinner,
  Tab,
  Tabs,
  Badge,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import { useAuth } from "../../context/AuthContext";
import colors from "../../constants/colors";
import "./ProfilePage.css";

const ProfilePage = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  // User details form
  const [formData, setFormData] = useState({
    username: "",
    email: "",
  });

  // Password change form
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // States for feedback
  const [loading, setLoading] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [profileSuccess, setProfileSuccess] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [validated, setValidated] = useState(false);
  const [passwordValidated, setPasswordValidated] = useState(false);

  useEffect(() => {
    // Redirect if not logged in
    if (!user) {
      navigate("/login", { state: { from: "/perfil" } });
      return;
    }

    // Populate form with user data
    setFormData({
      username: user.username || "",
      email: user.email || "",
    });
  }, [user, navigate]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value,
    });
  };

  const handleProfileSubmit = async (e) => {
    if (!formData.username || !formData.email) return;
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    setLoading(true);
    setProfileError("");
    setProfileSuccess("");

    try {
      const response = await axiosInstance.put("/user/profile", formData);
      setUser(response);
      setProfileSuccess("Perfil atualizado com sucesso!");
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        "Erro ao atualizar perfil. Por favor tente novamente.";
      setProfileError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.stopPropagation();
      setPasswordValidated(true);
      return;
    }

    // Check if new password matches confirmation
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError("As palavras-passe não coincidem.");
      return;
    }

    setLoading(true);
    setPasswordError("");
    setPasswordSuccess("");

    try {
      await axiosInstance.post("/user/change-password", {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      setPasswordSuccess("Senha alterada com sucesso!");
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        "Erro ao alterar senha. Por favor tente novamente.";
      setPasswordError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status" className="my-5">
          <span className="visually-hidden">A carregar...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="mb-4">
        <Col>
          <h1>Meu Perfil</h1>
          <p className="text-muted">Gerir e atualizar as informações da tua conta</p>
        </Col>
      </Row>

      <Row>
        <Col lg={4} className="mb-4">
          <Card className="shadow-sm text-center p-4">
            <div className="mb-4">
              <div
                style={{
                  width: "100px",
                  height: "100px",
                  borderRadius: "50%",
                  backgroundColor: colors.secondary,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "2.5rem",
                  color: colors.light,
                  margin: "0 auto",
                }}
              >
                {user.username ? user.username.charAt(0).toUpperCase() : "U"}
              </div>
            </div>
            <h3>{user.username}</h3>
            <p className="text-muted">{user.email}</p>
            {user.isAdmin && (
              <Badge
                className="mb-3"
                style={{
                  backgroundColor: colors.secondary,
                  color: colors.primary,
                }}
              >
                Administrador
              </Badge>
            )}
          </Card>
        </Col>

        <Col lg={8}>
          <Card className="shadow-sm">
            <Card.Body>
              <Tabs 
                defaultActiveKey="profile" 
                className="mb-4" 
                variant="pills"
              >
                <Tab 
                  eventKey="profile" 
                  title="Perfil"
                  tabClassName="custom-tab"
                >
                  {profileError && (
                    <Alert variant="danger">{profileError}</Alert>
                  )}
                  {profileSuccess && (
                    <Alert variant="success">{profileSuccess}</Alert>
                  )}

                  <Form
                    noValidate
                    validated={validated}
                    onSubmit={handleProfileSubmit}
                  >
                    <Form.Group className="mb-3" controlId="formUsername">
                      <Form.Label>Nome de Utilizador</Form.Label>
                      <Form.Control
                        type="text"
                        name="username"
                        value={formData.username}
                        onChange={handleProfileChange}
                        required
                        disabled={loading}
                      />
                      <Form.Control.Feedback type="invalid">
                        Por favor insira um nome de utilizador.
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formEmail">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleProfileChange}
                        required
                        disabled={loading}
                      />
                      <Form.Control.Feedback type="invalid">
                        Por favor insira um email válido.
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Button
                      type="submit"
                      disabled={loading}
                      style={{
                        backgroundColor: colors.secondary,
                        borderColor: colors.primary,
                      }}
                    >
                      {loading ? <>A guardar...</> : <>Guardar alterações</>}
                    </Button>
                  </Form>
                </Tab>

                <Tab 
                  eventKey="security" 
                  title="Segurança"
                  tabClassName="custom-tab"
                >
                  {passwordError && (
                    <Alert variant="danger">{passwordError}</Alert>
                  )}
                  {passwordSuccess && (
                    <Alert variant="success">{passwordSuccess}</Alert>
                  )}

                  <h5 className="mb-3">Alterar palavra-passe</h5>

                  <Form
                    noValidate
                    validated={passwordValidated}
                    onSubmit={handlePasswordSubmit}
                  >
                    <Form.Group
                      className="mb-3"
                      controlId="formCurrentPassword"
                    >
                      <Form.Label>Palavra-passe atual</Form.Label>
                      <Form.Control
                        type="password"
                        name="currentPassword"
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        required
                        disabled={loading}
                      />
                      <Form.Control.Feedback type="invalid">
                        Por favor insere a tua palavra-passe atual.
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formNewPassword">
                      <Form.Label>Nova palavra-passe</Form.Label>
                      <Form.Control
                        type="password"
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        required
                        pattern=".{6,}"
                        disabled={loading}
                      />
                      <Form.Control.Feedback type="invalid">
                        A palavra-passe deve ter pelo menos 6 caracteres.
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group
                      className="mb-3"
                      controlId="formConfirmPassword"
                    >
                      <Form.Label>Confirmar nova palavra-passe</Form.Label>
                      <Form.Control
                        type="password"
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        required
                        disabled={loading}
                      />
                      <Form.Control.Feedback type="invalid">
                        Por favor confirma a tua nova palavra-passe.
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Button
                      type="submit"
                      disabled={loading}
                      style={{
                        backgroundColor: colors.secondary,
                        borderColor: colors.primary,
                      }}
                    >
                      {loading ? "A atualizar..." : "Alterar palavra-passe"}
                    </Button>
                  </Form>
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ProfilePage;
